import { useState } from 'react';
import { Plus, X, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useCategories } from '@/contexts/CategoriesContext';
import { addTransaction } from '@/lib/firestore';
import type { TransactionInput } from '@/types';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error' | 'warning';
}

interface QuickAddTransactionProps {
  onSuccess?: () => void;
}

let toastCounter = 0;

export function QuickAddTransaction({ onSuccess }: QuickAddTransactionProps) {
  const { user } = useAuth();
  const { settings } = useSettings();
  const { gainCategories, expenseCategories, getCategoryLabel: getCtxLabel } = useCategories();

  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<'gain' | 'expense'>('expense');
  const [categoryId, setCategoryId] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(MONTHS[new Date().getMonth()]);

  const language = settings?.language || 'en';
  const categories = type === 'gain' ? gainCategories : expenseCategories;

  const showToast = (message: string, type: 'success' | 'error' | 'warning') => {
    const id = ++toastCounter;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  };

  const dismissToast = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const getCategoryLabel = (catId: string): string => getCtxLabel(catId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      showToast(
        language === 'fr'
          ? 'Non authentifié. Veuillez vous reconnecter.'
          : 'Not authenticated. Please log in again.',
        'error'
      );
      return;
    }

    if (!name.trim()) {
      showToast(
        language === 'fr' ? 'Veuillez entrer un nom' : 'Please enter a name',
        'warning'
      );
      return;
    }

    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum <= 0) {
      showToast(
        language === 'fr'
          ? 'Veuillez entrer un montant valide'
          : 'Please enter a valid amount',
        'warning'
      );
      return;
    }

    if (!categoryId) {
      showToast(
        language === 'fr'
          ? 'Veuillez sélectionner une catégorie'
          : 'Please select a category',
        'warning'
      );
      return;
    }

    setSubmitting(true);

    try {
      const today = new Date();
      const year = today.getFullYear();

      const transaction: TransactionInput = {
        name: name.trim(),
        amount: amountNum,
        type,
        categoryId,
        date: today,
      };

      if (notes.trim()) {
        transaction.notes = notes.trim();
      }

      console.log('💰 Quick adding transaction:', transaction, 'to month:', selectedMonth);

      await addTransaction(user.uid, year, selectedMonth, transaction);

      console.log('✅ Transaction added successfully!');

      setName('');
      setAmount('');
      setNotes('');
      setCategoryId('');
      setType('expense');
      setSelectedMonth(MONTHS[new Date().getMonth()]);

      setIsOpen(false);

      if (onSuccess) {
        onSuccess();
      }

      showToast(
        language === 'fr'
          ? `Transaction ajoutée en ${selectedMonth} !`
          : `Transaction added to ${selectedMonth}!`,
        'success'
      );
    } catch (error) {
      console.error('❌ Error adding quick transaction:', error);

      showToast(
        language === 'fr'
          ? "Échec de l'ajout de la transaction. Veuillez réessayer."
          : 'Failed to add transaction. Please try again.',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Toast Notifications */}
      <div className="fixed bottom-24 right-4 z-[200] flex flex-col gap-2 sm:bottom-28 sm:right-6 md:bottom-32 md:right-8">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center gap-3 rounded-2xl px-4 py-3 shadow-2xl transition-all max-w-xs ${
              toast.type === 'success'
                ? 'bg-emerald-600 text-white'
                : toast.type === 'error'
                  ? 'bg-red-600 text-white'
                  : 'bg-amber-500 text-white'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle className="h-5 w-5 shrink-0" />
            ) : (
              <AlertCircle className="h-5 w-5 shrink-0" />
            )}
            <span className="text-sm font-medium leading-snug">{toast.message}</span>
            <button
              onClick={() => dismissToast(toast.id)}
              className="ml-1 shrink-0 rounded-full p-0.5 opacity-70 hover:opacity-100 transition-opacity"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Floating Action Button (FAB) */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-2xl transition-all hover:scale-110 hover:shadow-blue-500/50 active:scale-95 sm:h-16 sm:w-16 md:bottom-8 md:right-8"
        aria-label={language === 'fr' ? 'Ajouter une transaction rapide' : 'Quick add transaction'}
      >
        <Plus className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={2.5} />
      </button>

      {/* Quick Add Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl dark:bg-slate-800">
            {/* Header */}
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                ⚡ {language === 'fr' ? 'Ajout Rapide' : 'Quick Add'}
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-lg p-2 text-slate-600 transition-all hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
              >
                <X size={24} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                {/* Type Selection */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    {language === 'fr' ? 'Type' : 'Type'}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => { setType('gain'); setCategoryId(''); }}
                      className={`rounded-xl px-4 py-3 font-semibold transition-all ${
                        type === 'gain'
                          ? 'bg-emerald-600 text-white shadow-lg'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
                      }`}
                    >
                      💰 {language === 'fr' ? 'Revenu' : 'Income'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setType('expense'); setCategoryId(''); }}
                      className={`rounded-xl px-4 py-3 font-semibold transition-all ${
                        type === 'expense'
                          ? 'bg-red-600 text-white shadow-lg'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
                      }`}
                    >
                      💳 {language === 'fr' ? 'Dépense' : 'Expense'}
                    </button>
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    {language === 'fr' ? 'Nom' : 'Name'}
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={language === 'fr' ? 'ex: Café' : 'e.g., Coffee'}
                    className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-700 dark:text-slate-100"
                    required
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    {language === 'fr' ? 'Montant' : 'Amount'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-700 dark:text-slate-100"
                    required
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    {language === 'fr' ? 'Catégorie' : 'Category'}
                  </label>
                  <select
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-700 dark:text-slate-100"
                    required
                  >
                    <option value="">
                      {language === 'fr' ? 'Sélectionner une catégorie...' : 'Select category...'}
                    </option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Month Selector */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    {language === 'fr' ? 'Mois' : 'Month'}
                  </label>
                  <div className="grid grid-cols-6 gap-1.5">
                    {MONTHS.map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setSelectedMonth(m)}
                        className={`rounded-lg py-2 text-xs font-semibold transition-all ${
                          selectedMonth === m
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes (Optional) */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    {language === 'fr' ? 'Notes' : 'Notes'}
                    <span className="ml-1 text-xs text-slate-500">
                      ({language === 'fr' ? 'optionnel' : 'optional'})
                    </span>
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={language === 'fr' ? 'Ajouter une note...' : 'Add a note...'}
                    rows={3}
                    className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-700 dark:text-slate-100"
                  />
                </div>

                {/* Info */}
                <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    ℹ️{' '}
                    {language === 'fr'
                      ? `Cette transaction sera ajoutée en ${selectedMonth}`
                      : `This transaction will be added to ${selectedMonth}`}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="flex-1 rounded-xl bg-slate-200 px-6 py-3 font-semibold text-slate-700 transition-all hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                >
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-blue-700 hover:to-purple-700 disabled:opacity-50"
                >
                  {submitting
                    ? language === 'fr' ? 'Ajout...' : 'Adding...'
                    : language === 'fr' ? '✅ Ajouter' : '✅ Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}